import { forwardRef } from 'react';
import { motion, type HTMLMotionProps } from 'framer-motion';
import { cn } from '@/lib/utils/cn';
import { spring } from '@/lib/motion/presets';

type Variant = 'primary' | 'ghost' | 'subtle';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'children'> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  children: React.ReactNode;
}

const base =
  'relative inline-flex items-center justify-center gap-2 font-medium select-none ' +
  'transition-[background,border,color,box-shadow] duration-150 ' +
  'disabled:cursor-not-allowed disabled:opacity-50 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-bg';

const variants: Record<Variant, string> = {
  primary:
    'bg-ink text-bg hover:bg-ink/90 shadow-card active:translate-y-px',
  ghost:
    'bg-transparent text-ink-muted hover:text-ink hover:bg-surface/50 border border-line',
  subtle:
    'bg-surface text-ink border border-line hover:border-line-strong hover:bg-surface-raised',
};

const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs rounded-md',
  md: 'h-10 px-4 text-sm rounded-md',
  lg: 'h-12 px-6 text-base rounded-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'subtle', size = 'md', loading, children, disabled, ...rest }, ref) => {
    return (
      <motion.button
        ref={ref}
        whileHover={{ y: -1 }}
        whileTap={{ y: 0, scale: 0.985 }}
        transition={spring}
        disabled={disabled || loading}
        className={cn(base, variants[variant], sizes[size], className)}
        {...rest}
      >
        {loading && (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="size-4 rounded-full border-2 border-ink/30 border-t-ink animate-spin" />
          </span>
        )}
        <span className={cn('inline-flex items-center gap-2', loading && 'opacity-0')}>
          {children}
        </span>
      </motion.button>
    );
  },
);
Button.displayName = 'Button';